import { z } from 'zod';
import { registry } from '../registry';
export const healthManagementCardTitles = {
    root: 'Health Management',
    notifications: 'Notifications:',
};
export const baseHealthManagementMedications = z
    .object({
    actions: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Medication actions',
        options: [
            { value: 'medicationsReconciled', label: 'Medications reconciled' },
            { value: 'newChangedMedications', label: 'New/changed medications in the home' },
            { value: 'pillBoxPrefilled', label: 'Pill box prefilled' },
            { value: 'insulinSyringesPrefilled', label: 'Insulin syringes prefilled' },
        ],
    }),
    issuesIdentified: z.boolean().optional().register(registry, { label: 'Medication issues identified:' }),
    issues: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Medication issues',
        options: [
            { value: 'drugInteraction', label: 'Drug interaction' },
            { value: 'duplicateTherapy', label: 'Duplicate therapy' },
            { value: 'ineffectiveDrugTherapy', label: 'Ineffective drug therapy' },
            { value: 'missingDrugs', label: 'Missing drugs from ordered regimen' },
            { value: 'nonAdherence', label: 'Non adherence' },
            { value: 'tooMuch', label: 'Taking more medication than prescribed' },
            { value: 'tooLittle', label: 'Taking less medication than prescribed' },
        ],
    }),
    issuesDescription: z.string().optional().register(registry, { label: 'Medication(s) issues and descriptions identified:' }),
    issuesNotifications: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: healthManagementCardTitles.notifications,
        options: [
            { value: 'physicianNotified', label: 'Physician notified: medication issue identified' },
            {
                value: 'clinicalManagerNotified',
                label: 'Clinical Manager notified: medication issue identified',
            },
        ],
    }),
})
    .register(registry, { label: 'Medications' });
export const baseHealthManagementHomeEnvironment = z
    .object({
    altered: z.boolean().optional().register(registry, { label: 'Home environment altered:' }),
    details: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Home environment details',
        options: [
            { value: 'ableToOnlyBuyNecessities', label: 'Able to only buy necessities' },
            { value: 'caregiverBurnout', label: 'Caregiver burnout' },
            { value: 'clutteredSoiledLivingConditions', label: 'Cluttered/soiled living conditions' },
            { value: 'difficultBuyingNecessities', label: 'Difficult buying necessities' },
            { value: 'inadequateCoolingHeating', label: 'Inadequate cooling/heating' },
            { value: 'lackOfCaregiverFamilySupport', label: 'Lack of caregiver/family support' },
            { value: 'limitedSocialContact', label: 'Limited social contact' },
            { value: 'lowNoIncome', label: 'Low/no income' },
            { value: 'poorHomeEnvironment', label: 'Poor home environment' },
        ],
    }),
})
    .register(registry, { label: 'Home environment' });
export const baseHealthManagementSuspectedAbuse = z
    .object({
    identified: z.boolean().optional().register(registry, { label: 'Suspected abuse:' }),
    types: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Suspected abuse types',
        options: [
            { value: 'exploitationOfFunds', label: 'Exploitation of funds' },
            { value: 'fearfulOfFamilyMember', label: 'Fearful of family member' },
            { value: 'inadequateFood', label: 'Inadequate food' },
            { value: 'leftUnattended', label: 'Left unattended when supervision required' },
            { value: 'neglect', label: 'Neglect' },
            { value: 'sexualAbuse', label: 'Sexual abuse' },
            { value: 'unexplainedBruises', label: 'Unexplained bruises/injuries' },
            { value: 'apsReferralMade', label: 'APS referral made' },
            { value: 'other', label: 'Other' },
        ],
    }),
    otherDescription: z.string().optional().register(registry, { label: 'Description' }),
})
    .register(registry, { label: 'Suspected abuse' });
export const baseHealthManagementBarriersToHealthStatus = z
    .object({
    identified: z.boolean().optional().register(registry, { label: 'Barriers to health status:' }),
    barriers: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Barriers',
        options: [
            { value: 'costOutOfPocket', label: 'Cost/out of pocket expenses' },
            { value: 'difficultBuyingMedications', label: 'Difficult buying medications' },
            { value: 'educationalDeficits', label: 'Educational deficits/low literacy' },
            { value: 'lackOfTrustWithProvider', label: 'Lack of satisfaction/trust with health provider' },
            { value: 'lackOfTransportation', label: 'Lack of transportation' },
            { value: 'multipleComorbidity', label: 'Multiple co-morbidity' },
            { value: 'multipleHealthProviders', label: 'Multiple health providers' },
            {
                value: 'perceptionOfLackingProviderResponse',
                label: 'Perception of lacking health provider response',
            },
            { value: 'poorSelfConcept', label: 'Poor self-concept' },
        ],
    }),
})
    .register(registry, { label: 'Barriers to health status' });
export const baseHealthManagementSignsOfHeartFailure = z
    .object({
    present: z.boolean().optional().register(registry, { label: 'Exhibiting signs/symptoms of heart failure:' }),
    actions: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Heart failure actions',
        options: [
            { value: 'physicianContacted', label: 'Physician contacted' },
            { value: 'advisedToSeekEmergencyTreatment', label: 'Advised to seek emergency treatment' },
            { value: 'followedEstablishedParameters', label: 'Followed established parameters' },
            { value: 'patientEducation', label: 'Patient education' },
            { value: 'receivedOrdersToChangePoc', label: 'Received orders to change POC' },
            {
                value: 'physicianNotified',
                label: 'Physician notified: patient exhibiting s/s of heart failure',
            },
            {
                value: 'clinicalManagerNotified',
                label: 'Clinical Manager notified: patient exhibiting s/s of heart failure',
            },
        ],
    }),
    comment: z.string().optional().register(registry, { label: 'Comment' }),
})
    .register(registry, { label: 'Signs of heart failure' });
export const baseHealthManagementSignsOfOtherComorbidity = z
    .object({
    present: z.boolean().optional().register(registry, { label: 'Exhibiting signs/symptoms of other co-morbidity:' }),
    actions: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Other co-morbidity actions',
        options: [
            { value: 'advisedToSeekEmergencyTreatment', label: 'Advised to seek emergency treatment' },
            { value: 'followedEstablishedParameters', label: 'Followed established parameters' },
            { value: 'patientEducation', label: 'Patient education' },
            { value: 'receivedOrdersToChangePoc', label: 'Received orders to change POC' },
            {
                value: 'physicianNotified',
                label: 'Physician notified: exhibiting s/s of other co-morbidity',
            },
            {
                value: 'clinicalManagerNotified',
                label: 'Clinical Manager notified: exhibiting s/s of other co-morbidity',
            },
        ],
    }),
    comment: z.string().optional().register(registry, { label: 'Comment' }),
})
    .register(registry, { label: 'Signs of other co-morbidity' });
export const baseHealthManagementSchema = z
    .object({
    medications: baseHealthManagementMedications.optional(),
    homeEnvironment: baseHealthManagementHomeEnvironment.optional(),
    suspectedAbuse: baseHealthManagementSuspectedAbuse.optional(),
    barriersToHealthStatus: baseHealthManagementBarriersToHealthStatus.optional(),
    signsOfHeartFailure: baseHealthManagementSignsOfHeartFailure.optional(),
    signsOfOtherComorbidity: baseHealthManagementSignsOfOtherComorbidity.optional(),
    comment: z.string().optional().register(registry, { label: 'Comment' }),
})
    .register(registry, { label: healthManagementCardTitles.root });
